import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import {
  Dialog,
  DialogAction,
  DialogActions,
  DialogClose,
  DialogContent,
} from "components/Dialog";
import { errorText } from "components/Forms/formsApi";
import { formatDate } from "components/Forms/ResponseDialog";
import type { PillColor } from "components/PersonPill";
import "components/PersonPill/index.css";
import CopyLinkRow from "components/PublicBooking/CopyLinkRow";
import { Tooltip, TooltipTrigger } from "components/Tooltip";
import { ReactNode, useState } from "react";
import { LuChevronDown } from "react-icons/lu";
import { SiOpenai, SiZapier } from "react-icons/si";
import Notifications from "util/notifications";
import { PeoplePills } from "./ActivityTabParts";
import { ReactComponent as ClaudeLogo } from "./assets/claude.svg";
import {
  MCP_CHANGES,
  MCP_READS,
  ZAPIER_ACTIONS,
  ZAPIER_EVENTS,
} from "./ConnectedAppsFacts";
import {
  LoadingRows,
  useAutomationsApi,
  useConnectedApps,
  useReference,
} from "./hooks";
import { nameOf } from "./reference";
import type { AppName, AppUser, ConnectedApp, ConnectedApps } from "./types";

type AppStatus = "connected" | "off" | "not_connected";

function statusOf(connection: ConnectedApp | undefined): AppStatus {
  if (!connection?.connected) return "not_connected";
  return connection.off ? "off" : "connected";
}

const STATUS: Record<AppStatus, { label: string; color: PillColor }> = {
  connected: { label: "Connected", color: "green" },
  off: { label: "Off", color: "gray" },
  not_connected: { label: "Not connected", color: "gray" },
};

/** Every logo at one size. */
const LOGO = "h-7 w-7 flex-none";

interface AppInfo {
  app: AppName;
  name: string;
  /** The app's own logo, in its own color. */
  logo: ReactNode;
  lists: { label: string; items: string[] }[];
  steps: ReactNode[];
  /** What Disconnect does, said in its confirm. */
  disconnects: string;
}

/** A value to enter, with Copy, in white on the panel's gray. */
function CopyValue({ value, copied }: { value: string; copied: string }) {
  return (
    <div className="mt-1 [&>div]:bg-white">
      <CopyLinkRow url={value} open={false} copied={copied} />
    </div>
  );
}

/**
 * A step that enters a value the backend gives: its words, then the value
 * with Copy. Without the value, its words only.
 */
function entered(
  words: string,
  value: string | null | undefined,
  copied: string
) {
  if (!value) return words;
  return (
    <>
      {words}
      <CopyValue value={value} copied={copied} />
    </>
  );
}

/**
 * Claude, ChatGPT, then Zapier, with what the backend gives for connecting
 * each: the MCP address and OAuth client ID that Claude's and ChatGPT's
 * pages ask for, and Zapier's page. The steps follow support.claude.com
 * 11175166 (with claude.com/docs/connectors/custom/remote-mcp for Advanced
 * settings and the blank secret) and help.openai.com 12584461 (with 20001249
 * for the OAuth fields and developers.openai.com/plugins/build/auth for the
 * none method). The client IDs are public Auth0 clients, so there is no
 * secret.
 */
function appsInfo(apps: ConnectedApps | null): AppInfo[] {
  const zapier = apps?.zapier.link ?? null;
  return [
    {
      app: "claude",
      name: "Claude",
      // title="" leaves out the file's own title: the name is beside it.
      logo: <ClaudeLogo className={LOGO} fill="#D97757" title="" aria-hidden />,
      lists: [
        { label: "Can read", items: MCP_READS },
        { label: "Can change", items: MCP_CHANGES },
      ],
      steps: [
        "In Claude on the web or desktop, open Customize > Connectors and click + Add, then Add custom connector. On Team or Enterprise, an Owner or Primary Owner opens Organization settings > Connectors, clicks Add, hovers over Custom and selects Web.",
        entered(
          "Name it Tadpole, enter this URL and click Continue.",
          apps?.claude.link,
          "URL copied"
        ),
        "Review the authentication settings Claude detected and click Continue. Under Authentication, choose Sign in now.",
        entered(
          "Under OAuth client, choose Use your own OAuth client, enter this client ID, leave the secret blank, and click Add. If the dialog is one screen, the client ID goes under Advanced settings.",
          apps?.claude.clientId,
          "Client ID copied"
        ),
        "In Customize > Connectors, click Connect on Tadpole and sign in with your Tadpole login.",
        "In a chat, click +, then Connectors, and turn on Tadpole.",
      ],
      disconnects:
        "Claude can't read or change anything in your organization until you connect it again.",
    },
    {
      app: "chatgpt",
      name: "ChatGPT",
      logo: <SiOpenai className={LOGO} color="#000000" aria-hidden />,
      lists: [
        { label: "Can read", items: MCP_READS },
        { label: "Can change", items: MCP_CHANGES },
      ],
      steps: [
        "In ChatGPT on the web, open Plugins and click +, then Add custom MCP server. An admin or owner on Business, Enterprise or Edu does this. A Pro connection can only read.",
        entered(
          "Name it Tadpole and enter this URL under Connection.",
          apps?.chatgpt.link,
          "URL copied"
        ),
        entered(
          "Set authentication to OAuth. Under Advanced OAuth settings, choose User-Defined OAuth Client, enter this OAuth client ID, leave the OAuth client secret blank, and set Token endpoint auth method to none.",
          apps?.chatgpt.clientId,
          "Client ID copied"
        ),
        "Check I understand and want to continue, then click Create as a plugin.",
        "Install the plugin. When ChatGPT asks, sign in with your Tadpole login.",
        "In a chat, type @ and pick Tadpole.",
        "To share it with your workspace, an admin or owner opens Admin Console > Plugins > Manage Legacy Apps > Drafts, clicks Publish and reviews the warnings.",
      ],
      disconnects:
        "ChatGPT can't read or change anything in your organization until you connect it again.",
    },
    {
      app: "zapier",
      name: "Zapier",
      logo: <SiZapier className={LOGO} color="#FF4F00" aria-hidden />,
      lists: [
        { label: "Events", items: ZAPIER_EVENTS },
        { label: "Actions", items: ZAPIER_ACTIONS },
      ],
      steps: [
        zapier ? (
          <>
            In Zapier, make a Zap with Tadpole:{" "}
            <a
              href={zapier}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-green-700 hover:underline"
            >
              Open Zapier
            </a>
          </>
        ) : (
          "In Zapier, make a Zap with Tadpole."
        ),
        "Sign in with your Tadpole login and pick your organization.",
        "Turn the Zap on.",
      ],
      disconnects:
        "Your Zaps stop getting events and can't act in Tadpole until you connect again.",
    },
  ];
}

/** A small heading inside a card. */
function Heading({ children }: { children: ReactNode }) {
  return <h4 className="text-sm font-bold text-gray-900">{children}</h4>;
}

/**
 * A list as a chip with its name, as a column's timing chip looks: the
 * list opens under it on hover and on focus, and stays open on click or
 * tap.
 */
function ListChip({ label, items }: { label: string; items: string[] }) {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[13px] font-semibold leading-5 text-gray-900 hover:border-gray-400 data-[state=delayed-open]:border-green-500 data-[state=instant-open]:border-green-500"
          onClick={(e) => {
            e.preventDefault(); // keep it open: the tooltip closes on click
            setOpen(true);
          }}
        >
          {label}
          <LuChevronDown
            aria-hidden
            strokeWidth={2.5}
            className="h-3 w-3 flex-none text-gray-500"
          />
        </button>
      </TooltipTrigger>
      {/* The app's tooltip, opening under the chip from its left edge. */}
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          className="TooltipContent"
          side="bottom"
          align="start"
          sideOffset={5}
          collisionPadding={12}
        >
          <ul className="m-0 w-max max-w-xs list-[disc] text-pretty pl-[18px] text-sm leading-[22px] text-gray-700 marker:text-gray-400">
            {items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <TooltipPrimitive.Arrow className="TooltipArrow" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </Tooltip>
  );
}

/** Who used the app in the organization, most recent first, in a compact boxed table. */
function RecentUse({ users }: { users: AppUser[] }) {
  const ref = useReference();
  return (
    <div className="mt-6">
      <Heading>Recent use</Heading>
      <div className="mt-2 overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              {["Who", "Last used", "Calls"].map((heading) => (
                <th
                  key={heading}
                  scope="col"
                  className="px-3 py-2 font-semibold text-gray-900"
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((use) => (
              <tr
                key={`${use.who ?? use.name}`}
                className="border-b border-gray-200 last:border-0"
              >
                <td className="px-3 py-2 text-gray-900">
                  {use.who ? nameOf(ref, use.who) : use.name || "Someone"}
                </td>
                <td className="px-3 py-2 text-gray-600">
                  {formatDate(use.lastAt)}
                </td>
                <td className="px-3 py-2 text-gray-600">{use.calls}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AppCard({
  info,
  connection,
}: {
  info: AppInfo;
  connection: ConnectedApp | undefined;
}) {
  const ref = useReference();
  const api = useAutomationsApi();
  const [confirming, setConfirming] = useState(false);
  const status = statusOf(connection);
  const off = connection?.off ?? null;
  const pill = STATUS[status];
  // Connected by, or Disconnected by once it is off.
  const by = off ? off.by : connection?.connectedBy;
  const at = off ? off.at : connection?.connectedAt;
  const failed = (e: unknown) =>
    Notifications.error(errorText(e, "That could not be done."));

  const disconnect = () =>
    api
      .disconnectApp(info.app)
      .then(() => {
        setConfirming(false);
        Notifications.success(`${info.name} is disconnected.`);
      })
      .catch(failed);

  const connectAgain = () =>
    api
      .connectAppAgain(info.app)
      .then(() => Notifications.success(`${info.name} is connected again.`))
      .catch(failed);

  return (
    <section
      className="min-w-0 rounded-lg border border-gray-200 bg-white p-6"
      aria-labelledby={`app-${info.app}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          {info.logo}
          <h3
            id={`app-${info.app}`}
            className="text-xl font-semibold text-gray-900"
          >
            {info.name}
          </h3>
          <span className={`person-pill person-pill--${pill.color}`}>
            {status === "connected" && (
              <span
                className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-current align-[1px]"
                aria-hidden
              />
            )}
            {pill.label}
          </span>
        </div>
        {status === "connected" && (
          <Button
            color={ButtonColor.RED}
            fill={ButtonFill.HOLLOW}
            size={ButtonSize.SMALL}
            onClick={() => setConfirming(true)}
          >
            Disconnect
          </Button>
        )}
        {status === "off" && (
          <Button
            color={ButtonColor.GREEN}
            size={ButtonSize.SMALL}
            onClick={connectAgain}
          >
            Connect again
          </Button>
        )}
      </div>
      {connection?.connected && (
        <p className="mt-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-gray-600">
          {off ? "Disconnected" : "Connected"} by{" "}
          {by ? <PeoplePills people={[by]} /> : nameOf(ref, by)} on{" "}
          {formatDate(at)}.
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {info.lists.map((list) => (
          <ListChip key={list.label} label={list.label} items={list.items} />
        ))}
      </div>

      <div className="mt-5 max-w-lg rounded-lg border border-gray-200 bg-gray-50 p-5">
        <Heading>How to connect</Heading>
        <ol className="mt-2 list-[decimal] space-y-2 pl-5 text-sm leading-5 text-gray-800 marker:text-gray-500">
          {info.steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      </div>

      {!!connection?.users?.length && <RecentUse users={connection.users} />}

      <Dialog open={confirming} onOpenChange={setConfirming}>
        {confirming && (
          <DialogContent className="dialog-content">
            <h2 className="mb-2 text-xl font-semibold">
              Disconnect {info.name}?
            </h2>
            <p className="text-sm text-gray-700">{info.disconnects}</p>
            <DialogActions>
              <DialogAction
                primary={true}
                color={ButtonColor.RED}
                onClick={disconnect}
              >
                Disconnect
              </DialogAction>
              <DialogClose asChild>
                <DialogAction color={ButtonColor.SKYBLUE}>Cancel</DialogAction>
              </DialogClose>
            </DialogActions>
          </DialogContent>
        )}
      </Dialog>
    </section>
  );
}

/**
 * The Connected apps tab: Claude, ChatGPT and Zapier side by side (stacked
 * on a narrow screen), each a card with its logo, name and status
 * (Disconnect, and Connect again once it is off), who connected it, what it
 * can do (each list a chip that opens it), How to connect under the logo,
 * and who used it. A card ends where its content ends.
 */
export default function ConnectedAppsTab() {
  const { apps, loading, error } = useConnectedApps();
  if (loading) return <LoadingRows />;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  return (
    <div className="grid items-start gap-4 lg:grid-cols-3">
      {appsInfo(apps).map((info) => (
        <AppCard key={info.app} info={info} connection={apps?.[info.app]} />
      ))}
    </div>
  );
}
